import {
  PayOnAccountPaymentPreviewV1Response,
  PaymentAccount,
  PaymentPreviewErrorPayload,
  RemittanceFile,
  RemittanceFileAttachmentData,
  resolvePadVerifiedStatus,
  roundTo2Decimals,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  BtPayPaymentAccountsActions,
  PayActions,
  PayOnAccountCoreComponentActions,
  PayOnAccountSelectPaymentMethodEmailActions,
  PoaBtPaySelectPaymentMethodActions,
  PoaPadResultComponentPadActions,
  PoaPadResultComponentPaymentPreviewActions,
  PoaPadResultComponentSessionActions,
  PoaPaymentDetailsPaymentInformationApiActions,
  PoaPaymentReceiptComponentActions,
  PoaSelectPaymentMethodComponentActions,
  PoaSelectPaymentMethodPadAgreementActions,
  PoaSelectPaymentMethodPaymentAccountActions,
} from '@arc/unified-payments-frontend/shared-stores';
import { createReducer, createFeature, on } from '@ngrx/store';
import { PayOnAccountPaymentDetails } from '../../models';
import {
  PayOnAccountActions,
  PayOnAccountReviewPaymentActions,
  PayOnAccountSelectPaymentMethodActions,
} from '../actions';
export interface PayOnAccountStoreState {
  paymentDetails: PayOnAccountPaymentDetails;
  paymentDate: Date;
  paymentPreview: PayOnAccountPaymentPreviewV1Response;
  selectedPaymentAccount: PaymentAccount;
  surchargeError: PaymentPreviewErrorPayload;
  isScheduledPaymentDate: boolean;
  isAddPaymentMethodDialogVisible: boolean;
  cvv: string;
  padAgreementRemittanceFile: RemittanceFileAttachmentData;
  redirectSelectedPaymentAccountBurn: string;
  isAccountIdentifierInvalid: boolean;
  // BTPAY-7254: set once by the Wallet "Back" landing page (payment-receipt component) on
  // arrival, so the reused confirmation screen can suppress the autopay enrollment banner
  // instead of re-showing it immediately after the buyer already came from it.
  arrivedViaReceiptLanding: boolean;
}

export const initialState: PayOnAccountStoreState = {
  paymentDetails: {
    amount: 0,
    buyerAccount: null,
    paymentDate: new Date(),
    paymentNotes: null,
    receiptRecipients: null,
    remittanceFile: null,
    referenceDate: null,
    referenceNumber: null,
  },

  paymentDate: null,
  paymentPreview: null,
  selectedPaymentAccount: null,
  surchargeError: null,
  isScheduledPaymentDate: false,
  isAddPaymentMethodDialogVisible: false,
  cvv: null,
  padAgreementRemittanceFile: null,
  redirectSelectedPaymentAccountBurn: null,
  isAccountIdentifierInvalid: false,
  arrivedViaReceiptLanding: false,
};

export const payOnAccountFeature = createFeature({
  name: 'payOnAccount',
  reducer: createReducer(
    initialState,
    on(
      PayOnAccountCoreComponentActions.createPayOnAccountPaymentPreviewCompleteAction,
      (state, action) => ({
        ...state,
        paymentPreview: action.payload,
      }),
    ),
    on(
      PoaPaymentReceiptComponentActions.loadPaymentPreviewSuccess,
      (state, action): PayOnAccountStoreState => ({
        ...state,
        paymentPreview: action.payload,
        // The live flow populates paymentDetails.buyerAccount from the Payment Details form
        // (step 1), which a cold Wallet "Back" landing never runs — synthesize it from the
        // preview response instead, so selectReviewPaymentData doesn't crash reading
        // paymentDetails.buyerAccount.accountNumber off a null buyerAccount.
        paymentDetails: {
          ...state.paymentDetails,
          buyerAccount: {
            ...state.paymentDetails.buyerAccount,
            accountNumber: action.payload.payments[0]?.account
              ?.buyerAccountIdentifier
              ? action.payload.payments[0].account.buyerAccountIdentifier.replace(
                  'accountNumber:',
                  '',
                )
              : undefined,
          },
        },
      }),
    ),
    on(
      PoaPaymentReceiptComponentActions.loadSessionSummarySuccess,
      (state, action): PayOnAccountStoreState => ({
        ...state,
        // The receipt-landing preview recompute doesn't echo back the buyer's original
        // payment note (payments[0].payment.note comes back empty) — the session summary's
        // own payment.paymentNotes does have it, so synthesize it here as a fallback source
        // for selectReviewPaymentData.
        paymentDetails: {
          ...state.paymentDetails,
          paymentNotes: action.payload.payment?.paymentNotes,
        },
      }),
    ),
    on(
      PoaPaymentReceiptComponentActions.initialized,
      (state): PayOnAccountStoreState => ({
        ...state,
        arrivedViaReceiptLanding: true,
      }),
    ),
    on(
      PoaPaymentReceiptComponentActions.selectedPaymentAccountLoaded,
      (state, action): PayOnAccountStoreState => ({
        ...state,
        selectedPaymentAccount: action.selectedPaymentAccount,
      }),
    ),
    on(
      PoaPadResultComponentPaymentPreviewActions.restoreCustomTemplateDataFromPreview,
      (state, action) => ({
        ...state,
        paymentDetails: {
          ...state.paymentDetails,
          customTemplateData: action.payload,
        },
      }),
    ),
    on(PayOnAccountActions.PaymentDetailsSubmittedAction, (state, action) => ({
      ...state,
      paymentDetails: action.paymentDetails,
      padAgreementRemittanceFile:
        action.paymentDetails?.remittanceFile?.name !==
        state.padAgreementRemittanceFile?.fileName
          ? null
          : state.padAgreementRemittanceFile,
    })),
    on(
      PoaSelectPaymentMethodComponentActions.paymentAccountSelected,
      PoaSelectPaymentMethodPaymentAccountActions.createPaymentAccountSuccess,
      (state, action) => {
        return {
          ...state,
          // We should only maintain the saved cvv when:
          // 1. We navigate back to the selected payment method step
          // 2. And had a payment account previously selected
          cvv:
            state.selectedPaymentAccount?.paymentAccountIdentifierBurn ==
            action.payload?.paymentAccountIdentifierBurn
              ? state.cvv
              : null,
          selectedPaymentAccount: action.payload,
        };
      },
    ),
    on(
      PoaSelectPaymentMethodComponentActions.paymentAccountSelected,
      PoaSelectPaymentMethodPaymentAccountActions.createPaymentAccountSuccess,
      PoaSelectPaymentMethodComponentActions.selectedPaymentAccountDeleted,
      (state) => ({
        ...state,
        paymentPreview: {
          ...state.paymentPreview,
          payments: state.paymentPreview?.payments.map((p) => {
            return {
              ...p,
              payment: {
                ...p.payment,
                totalAmount: roundTo2Decimals(
                  p.payment.totalAmount - p.payment.totalSurcharge,
                ),
                totalSurcharge: 0,
              },
            };
          }),
        },
      }),
    ),
    on(
      PoaSelectPaymentMethodComponentActions.paymentAccountSelected,
      PoaSelectPaymentMethodPaymentAccountActions.createPaymentAccountSuccess,
      PoaSelectPaymentMethodComponentActions.selectedPaymentAccountDeleted,
      (state) => {
        return {
          ...state,
          surchargeError: null,
        };
      },
    ),
    on(
      PayOnAccountActions.PaymentPreviewSurchargeUpdatedAction,
      (state, action) => ({
        ...state,
        paymentPreview: action.paymentPreview?.payments?.length
          ? action.paymentPreview
          : null,
      }),
    ),
    on(PayOnAccountActions.SurchargeAmountErrorAction, (state, action) => {
      return {
        ...state,
        surchargeError: {
          isSelectedCardRestrictedByType:
            action.payload.isSelectedCardRestrictedByType,
          isSelectedCardRestrictedByZipCode:
            action.payload.isSelectedCardRestrictedByZipCode,
          error: action.payload.error as string,
        },
      };
    }),
    on(PayOnAccountActions.OpenAddPaymentMethodDialog, (state) => ({
      ...state,
      isAddPaymentMethodDialogVisible: true,
    })),
    on(PayOnAccountActions.closeAddPaymentMethodDialog, (state) => ({
      ...state,
      isAddPaymentMethodDialogVisible: false,
    })),
    on(
      PayOnAccountActions.IsScheduledPaymentDateUpdated,
      (state, { isScheduledPaymentDate }) => ({
        ...state,
        isScheduledPaymentDate: isScheduledPaymentDate,
      }),
    ),
    on(PayOnAccountActions.cvvUpdated, (state, { cvv }) => ({
      ...state,
      cvv,
    })),
    on(
      PoaBtPaySelectPaymentMethodActions.sensitiveInformationCleared,
      (state) => ({
        ...state,
        cvv: null,
        selectedPaymentAccount: {
          ...state.selectedPaymentAccount,
          paymentMethodFormDetails: null,
        },
      }),
    ),
    on(
      PoaSelectPaymentMethodComponentActions.selectedPaymentAccountDeleted,
      (state) => ({
        ...state,
        selectedPaymentAccount: null,
        cvv: null,
      }),
    ),
    on(
      PayOnAccountActions.paymentAccountExpirationDateUpdated,
      (state, { expirationDate }) => {
        return {
          ...state,
          selectedPaymentAccount: {
            ...state.selectedPaymentAccount,
            expirationMonth: expirationDate.expirationMonth,
            expirationYear: expirationDate.expirationYear,
          },
        };
      },
    ),
    on(
      PayOnAccountSelectPaymentMethodEmailActions.uploadAttachmentSuccess,
      (state, { attachmentUrlResponse, remittanceFile }) => {
        return {
          ...state,
          padAgreementRemittanceFile: {
            fileName: remittanceFile.name,
            fileType: remittanceFile.type,
            tenantId: attachmentUrlResponse.tenantId,
            applicationName: attachmentUrlResponse.applicationName,
            fileBucket: attachmentUrlResponse.fileBucket,
            fileKey: attachmentUrlResponse.fileKey,
            uploadUrl: attachmentUrlResponse.uploadUrl,
            urlExpiration: attachmentUrlResponse.urlExpiration,
          },
        };
      },
    ),
    on(
      PayOnAccountReviewPaymentActions.paymentPreviewPaymentReferenceUpdated,
      PoaPadResultComponentPaymentPreviewActions.loadPaymentPreviewSuccess,
      (state, { payload }) => ({
        ...state,
        paymentPreview: payload,
      }),
    ),
    on(PayActions.ValidatePaymentDateCompleteAction, (state, action) => ({
      ...state,
      paymentDate: action.paymentDateValidationResult.newPaymentDate,
    })),
    on(
      PayOnAccountSelectPaymentMethodActions.PaymentDateUpdated,
      (state, { updatedPaymentDate }) => {
        return {
          ...state,
          paymentDate: updatedPaymentDate,
        };
      },
    ),
    on(
      PoaPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
      (state, action) => ({
        ...state,
        paymentDetails: {
          amount: action.payload.redirectState.paymentDetails.amount,
          buyerAccount:
            action.payload.redirectState.paymentDetails.buyerAccount,
          paymentDate: new Date(
            action.payload.redirectState.paymentDetails.paymentDate,
          ),
          paymentNotes:
            action.payload.redirectState.paymentDetails.paymentNotes,
          receiptRecipients:
            action.payload.redirectState.paymentDetails.receiptRecipients,
          referenceDate: new Date(
            action.payload.redirectState.paymentDetails.referenceDate,
          ),
          referenceNumber:
            action.payload.redirectState.paymentDetails.referenceNumber,
          remittanceFile: action.payload.redirectState.paymentDetails
            .remittanceFileAttachmentData
            ? ({
                content: new ArrayBuffer(0),
                name: action.payload.redirectState.paymentDetails
                  .remittanceFileAttachmentData.fileName,
                type: action.payload.redirectState.paymentDetails
                  .remittanceFileAttachmentData.fileType,
              } as RemittanceFile)
            : undefined,
        },
        paymentDate: new Date(action.payload.redirectState.paymentDate),
        redirectSelectedPaymentAccountBurn:
          action.payload.redirectState.selectedPaymentAccountBurn,
        padAgreementRemittanceFile:
          action.payload.redirectState.paymentDetails
            .remittanceFileAttachmentData,
      }),
    ),
    on(
      BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsAfterRedirectSuccess,
      (state, action) => ({
        ...state,
        selectedPaymentAccount: action.paymentAccounts.find(
          (account) =>
            account.paymentAccountIdentifierBurn ===
            state.redirectSelectedPaymentAccountBurn,
        ),
      }),
    ),
    on(
      PoaSelectPaymentMethodPadAgreementActions.updatePadVerifiedStatusSuccess,
      (state, action) => ({
        ...state,
        selectedPaymentAccount: {
          ...state.selectedPaymentAccount,
          padVerifiedStatus: resolvePadVerifiedStatus(action.payload),
        },
      }),
    ),
    on(
      PoaPadResultComponentPadActions.completeTwikeyInviteSuccess,
      (state) => ({
        ...state,
        selectedPaymentAccount: {
          ...state.selectedPaymentAccount,
          padVerifiedStatus: {
            padSignedSeparately: false,
            padVerified: true,
          },
        },
      }),
    ),
    on(
      PoaSelectPaymentMethodComponentActions.remoteCardCaptureSuccess,
      (state) => {
        return {
          ...state,
          isAddPaymentMethodDialogVisible: false,
        };
      },
    ),
    on(PayOnAccountActions.validateAccountIdentifierSuccess, (state) => ({
      ...state,
      isAccountIdentifierInvalid: false,
    })),
    on(
      PayOnAccountActions.validateAccountIdentifierError,
      (state, { accountIdentifierInvalid }) => ({
        ...state,
        isAccountIdentifierInvalid: accountIdentifierInvalid,
      }),
    ),
  ),
});

export const {
  name,
  reducer,
  selectPayOnAccountState,
  selectPaymentPreview,
  selectPaymentDetails,
  selectPaymentDate,
  selectSelectedPaymentAccount,
  selectSurchargeError,
  selectIsScheduledPaymentDate,
  selectIsAddPaymentMethodDialogVisible,
  selectCvv,
  selectRedirectSelectedPaymentAccountBurn,
  selectIsAccountIdentifierInvalid,
  selectArrivedViaReceiptLanding,
} = payOnAccountFeature;
